/**
 * Named gesture-tuning presets.
 *
 * Centralising the magic numbers keeps the feel consistent across call sites and
 * makes tuning a one-line change. Each preset bundles the recognizer's
 * `fling`/`thresholdPx` with the consumer-facing layout constants (commit
 * thresholds, rubber-band limits, animation durations) the recognizer does not
 * own.
 */

import type { FlingConfig } from "./types";

const GESTURE_DEADZONE_PX = 8;

const STANDARD_FLING: FlingConfig = {
  maxDurationMs: 300,
  minDistancePx: 50,
  axisRatio: 2,
};

/**
 * Per-row swipe-to-action (e.g. delete a logged set, remove a routine
 * exercise). A single horizontal axis so it never fights vertical scroll.
 */
export const ROW_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 96,
  rubberBandLimitPx: 120,
  rubberBandFactor: 0.25,
  hapticMs: 15,
  commitAnimationMs: 260,
  cancelAnimationMs: 240,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Swipe-to-dismiss for a transient toast. Horizontal single axis so it never
 * fights vertical page scroll behind it. Dismissable in either direction; the
 * card follows the finger and commits past a fraction of its own width (or on a
 * fling), otherwise snaps back to center.
 */
export const TOAST_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 80,
  commitThresholdRatio: 0.4,
  rubberBandLimitPx: 9999,
  rubberBandFactor: 1,
  hapticMs: 12,
  commitAnimationMs: 220,
  cancelAnimationMs: 220,
  fallbackWidthPx: 400,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Horizontal carousel that pages between sibling panels (e.g. the exercises of
 * an active session). The track follows the finger and, on commit (or fling),
 * the current panel slides out while the neighbour slides in from the opposite
 * edge — same physics as the canon calendar/drawer carousels. Rubber-bands at
 * the ends (no wrap-around). Commit at the smaller of a fixed travel or a
 * fraction of the track width so it scales with viewport.
 */
export const CAROUSEL_SWIPE = {
  thresholdPx: GESTURE_DEADZONE_PX,
  commitThresholdPx: 64,
  commitThresholdRatio: 0.22,
  rubberBandLimitPx: 96,
  rubberBandFactor: 0.3,
  transitionMs: 260,
  fallbackWidthPx: 360,
  hapticMs: 12,
  fling: { ...STANDARD_FLING },
} as const;

/**
 * Mobile navigation drawer: drag it toward its own edge to dismiss it.
 *
 * The drawer is anchored to the left, so only a leftward drag means anything.
 * Dragging the other way has nothing to reveal — it gets a short, capped
 * rubber-band so the panel acknowledges the finger without ever detaching from
 * its edge or committing anything.
 */
export const NAV_DRAWER_DISMISS = {
  thresholdPx: GESTURE_DEADZONE_PX,
  /** Travel toward the edge that commits the close on release. */
  commitThresholdPx: 72,
  /** Give on the closed-direction drag (the one that does nothing). */
  rubberBandFactor: 0.25,
  /** Hard cap on that give. */
  rubberBandLimitPx: 24,
  hapticMs: 12,
  /** Used for the backdrop fade when the panel has not been measured yet. */
  fallbackWidthPx: 280,
  /** A flick dismisses without reaching `commitThresholdPx`. */
  fling: { maxDurationMs: 300, minDistancePx: 40, axisRatio: 2 } satisfies FlingConfig,
} as const;
